import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { Subject } from 'rxjs';

import { OAuthService } from '../../../shared/services/oauth.service';
import { LoggerService } from '../../../shared/services/logger.service';
import { environment } from '../../../../environments/environment';

/**
 * Delays between initial-connect attempts. `withAutomaticReconnect` only covers
 * connections that were established at least once, so a failed `start()` — e.g. a
 * 504 on `negotiate` while the API is cold-starting — would otherwise leave the
 * widget without live updates for the whole session.
 */
const START_RETRY_DELAYS_MS = [2000, 5000, 15000, 30000];

/** Negotiate failure that retrying cannot fix: the user may not use the hub. */
const FORBIDDEN = 403;
/** Negotiate failure fixed by renewing the token once (it expired or was revoked). */
const UNAUTHORIZED = 401;

@Injectable({
  providedIn: 'root'
})
export class AnnouncementHubService {
  private hubConnection: HubConnection | null = null;
  private startPromise: Promise<void> | null = null;
  /** Bumped by stopConnection() so an in-flight retry loop aborts instead of reconnecting. */
  private connectionGeneration = 0;
  private readonly announcementReceivedSubject = new Subject<number>();

  readonly announcementReceived$ = this.announcementReceivedSubject.asObservable();

  constructor(
    private readonly oauthService: OAuthService,
    private readonly logger: LoggerService
  ) {}

  async startConnection(): Promise<void> {
    if (this.hubConnection && this.hubConnection.state !== HubConnectionState.Disconnected) {
      return;
    }

    if (this.startPromise) {
      return this.startPromise;
    }

    this.startPromise = this.connectWithRetry().finally(() => {
      this.startPromise = null;
    });

    return this.startPromise;
  }

  async stopConnection(): Promise<void> {
    // Abort any retry loop that is waiting between attempts.
    this.connectionGeneration++;

    const connection = this.hubConnection;
    this.hubConnection = null;

    if (!connection) {
      return;
    }

    try {
      if (connection.state !== HubConnectionState.Disconnected) {
        await connection.stop();
      }
    } catch (error) {
      this.logger.errorWithContext('AnnouncementHubService', 'Failed to stop announcement hub connection', error);
    }
  }

  private async connectWithRetry(): Promise<void> {
    const generation = this.connectionGeneration;

    // No session (signed out, or the shell started before sign-in finished): don't
    // negotiate anonymously. AnnouncementNotificationService starts the hub again
    // on loginChanged(true).
    const token = await this.oauthService.getValidAccessToken();
    if (generation !== this.connectionGeneration || !token) {
      return;
    }

    const connection = this.buildConnection();
    this.hubConnection = connection;

    let attempt = 0;
    let renewedAfterUnauthorized = false;
    for (;;) {
      try {
        await connection.start();
        return;
      } catch (error) {
        // Widget was destroyed (or stopConnection called) while we were connecting.
        if (generation !== this.connectionGeneration) {
          return;
        }

        const statusCode = this.getStatusCode(error);

        // The token expired or was revoked between the factory call and the server's
        // check (typically a tab resuming from sleep). Renew once and try again before
        // giving up; a second 401 with a fresh token is a real problem.
        if (statusCode === UNAUTHORIZED && !renewedAfterUnauthorized) {
          renewedAfterUnauthorized = true;
          const renewed = await this.oauthService.getValidAccessToken({ forceRenewal: true });
          if (generation !== this.connectionGeneration) {
            return;
          }
          if (renewed) {
            continue;
          }
          // The session is gone; loginChanged(false) follows and a new sign-in restarts the hub.
          this.logger.warn('AnnouncementHubService', 'Announcement hub needs a new sign-in, not retrying');
          this.hubConnection = null;
          return;
        }

        if (statusCode === UNAUTHORIZED || statusCode === FORBIDDEN) {
          this.logger.errorWithContext(
            'AnnouncementHubService',
            'Announcement hub rejected the connection, not retrying',
            error
          );
          this.hubConnection = null;
          return;
        }

        if (attempt === START_RETRY_DELAYS_MS.length) {
          // Only report once, after every retry is spent — a transient cold-start
          // 504 that self-heals should not surface as an analytics error.
          this.logger.errorWithContext(
            'AnnouncementHubService',
            `Failed to connect to announcement hub after ${attempt + 1} attempts`,
            error
          );
          this.hubConnection = null;
          return;
        }

        const delayMs = START_RETRY_DELAYS_MS[attempt];
        attempt++;
        this.logger.warn(
          'AnnouncementHubService',
          `Announcement hub connection attempt ${attempt} failed, retrying in ${delayMs}ms`,
          error
        );
        await this.delay(delayMs);

        if (generation !== this.connectionGeneration) {
          return;
        }
      }
    }
  }

  /** Separate so tests can substitute the connection. */
  protected buildConnection(): HubConnection {
    const baseUrl = environment.apiRoot.replace('/api/', '').replace('/api', '');
    const hubUrl = `${baseUrl}/notificationHub`;

    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        // Called on every negotiate, including automatic reconnects after the tab slept,
        // so it renews an expired token instead of sending none (which the API 401s).
        accessTokenFactory: async () => {
          const token = await this.oauthService.getValidAccessToken();
          return token ?? '';
        }
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000])
      .configureLogging(environment.production ? LogLevel.Warning : LogLevel.Information)
      .build();

    connection.on('AnnouncementReceived', (announcementId: number) => {
      this.announcementReceivedSubject.next(announcementId);
    });

    // Automatic reconnect gives up after its last delay; fall back to the slower
    // retry loop so a long API outage doesn't permanently kill live updates.
    connection.onclose((error) => {
      if (this.hubConnection !== connection || !error) {
        return;
      }

      this.logger.warn('AnnouncementHubService', 'Announcement hub connection closed, reconnecting', error);
      this.hubConnection = null;
      void this.startConnection();
    });

    return connection;
  }

  /**
   * SignalR wraps negotiate failures in a `FailedToNegotiateWithServerError` whose
   * status code survives only in the message text, so fall back to matching on that.
   */
  private getStatusCode(error: unknown): number | null {
    const statusCode = (error as { statusCode?: number })?.statusCode;
    if (typeof statusCode === 'number') {
      return statusCode;
    }

    const message = error instanceof Error ? error.message : String(error ?? '');
    const match = /Status code '(\d+)'/.exec(message);
    return match ? Number(match[1]) : null;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
